/**
 * Piezas comunes de las pantallas de Administración, sobre `@routes-plan/ui` y Material UI:
 * acciones por fila con permiso, secciones de formulario, botones Cancelar/Guardar, rejilla de
 * filtros y confirmación con `ConfirmDialog`. Todo con colores del tema (claro y oscuro).
 */
import React, { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmDialog, LoadingButton, useNotify, type ConfirmVariant } from '@routes-plan/ui';
import { shouldHideForbiddenActions } from '../../../utils/permission-ui';
import { useT } from '../../../i18n';
import { accessErrorMessage } from '../../../utils/accessErrors';

/** Contenido de una pantalla de administración: ancho completo, sin desbordar a los lados. */
export const AdminPage: React.FC<{ children: React.ReactNode; narrow?: boolean }> = ({ children, narrow }) => (
  <Box
    sx={{
      width: '100%',
      minWidth: 0,
      maxWidth: narrow ? 1200 : '100%',
      mx: narrow ? 'auto' : undefined,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    {children}
  </Box>
);

/** Tarjeta que contiene una tabla o un bloque de contenido de la pantalla. */
export const ContentCard: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Card variant="outlined" sx={{ minWidth: 0, ...((sx as object) ?? {}) }}>
    <CardContent sx={{ minWidth: 0, '&:last-child': { pb: 2 } }}>{children}</CardContent>
  </Card>
);

/** Fila de filtros: columnas de al menos 240 px (hasta 4); en teléfono, uno debajo del otro. */
export const FilterRow: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        sm: 'repeat(auto-fill, minmax(240px, 1fr))',
        lg: 'repeat(4, minmax(0, 1fr))',
      },
      gap: 2,
      alignItems: 'center',
      minWidth: 0,
      ...((sx as object) ?? {}),
    }}
  >
    {children}
  </Box>
);

/** Sección de un formulario: borde, título h6, descripción opcional y campos en rejilla. */
export const FormSection: React.FC<{
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Columnas en md+ (1 en teléfono). Por defecto 2. */
  columns?: 1 | 2 | 3;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ title, description, children, columns = 2, action, sx }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, minWidth: 0, ...((sx as object) ?? {}) }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: description ? 0.5 : 2, flexWrap: 'wrap' }}>
        {title && (
          <Typography variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
        )}
        {action}
      </Box>
    )}
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
    )}
    <FieldGrid columns={columns}>{children}</FieldGrid>
  </Paper>
);

/** Campos en rejilla `minmax(0, 1fr)`: 1 columna en teléfono y `columns` desde md. */
export const FieldGrid: React.FC<{ children: React.ReactNode; columns?: 1 | 2 | 3 }> = ({ children, columns = 2 }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        md: `repeat(${columns}, minmax(0, 1fr))`,
      },
      minWidth: 0,
    }}
  >
    {children}
  </Box>
);

/** Ocupa toda la fila dentro de `FieldGrid`. */
export const FullRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>{children}</Box>
);

/** Acciones de un formulario abajo a la derecha: Cancelar (outlined) y Guardar (`LoadingButton`). */
export const FormActions: React.FC<{
  onCancel?: () => void;
  cancelTo?: string;
  saving?: boolean;
  /** Si es false, Guardar queda desactivado con `deniedTooltip`. */
  allowed?: boolean;
  deniedTooltip?: string;
  disabled?: boolean;
  saveLabel?: React.ReactNode;
  /** Botón Guardar como `type="submit"` del formulario (por defecto true). */
  submit?: boolean;
  onSave?: () => void;
  extra?: React.ReactNode;
}> = ({ onCancel, cancelTo, saving, allowed = true, deniedTooltip, disabled, saveLabel, submit = true, onSave, extra }) => {
  const t = useT();
  if (!allowed && shouldHideForbiddenActions() && !cancelTo && !onCancel) return null;
  const saveButton = (
    <LoadingButton
      variant="contained"
      type={submit ? 'submit' : 'button'}
      onClick={submit ? undefined : onSave}
      loading={saving}
      disabled={disabled || !allowed}
      data-tour="save"
    >
      {saveLabel ?? t('common.save')}
    </LoadingButton>
  );
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
      {extra && <Box sx={{ mr: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>{extra}</Box>}
      {cancelTo ? (
        <Button variant="outlined" color="inherit" component={RouterLink} to={cancelTo} disabled={saving}>
          {t('common.cancel')}
        </Button>
      ) : onCancel ? (
        <Button variant="outlined" color="inherit" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
      ) : null}
      {!allowed && shouldHideForbiddenActions() ? null : !allowed && deniedTooltip ? (
        <Tooltip title={deniedTooltip}>
          <span>{saveButton}</span>
        </Tooltip>
      ) : (
        saveButton
      )}
    </Box>
  );
};

/**
 * Acción por fila con ícono, descripción al pasar el mouse y `aria-label`. Sin permiso se oculta
 * (o queda desactivada con la descripción del permiso, según la marca).
 */
export const RowAction: React.FC<{
  label: string;
  icon: React.ReactNode;
  allowed?: boolean;
  deniedTooltip?: string;
  onClick?: () => void;
  to?: string;
  color?: 'default' | 'primary' | 'error' | 'success' | 'warning' | 'info';
  disabled?: boolean;
}> = ({ label, icon, allowed = true, deniedTooltip, onClick, to, color = 'default', disabled }) => {
  if (!allowed && shouldHideForbiddenActions()) return null;
  const off = disabled || !allowed;
  const button = to && !off ? (
    <IconButton size="small" color={color} aria-label={label} component={RouterLink} to={to}>
      {icon}
    </IconButton>
  ) : (
    <IconButton size="small" color={color} aria-label={label} onClick={off ? undefined : onClick} disabled={off}>
      {icon}
    </IconButton>
  );
  return (
    <Tooltip title={!allowed ? deniedTooltip ?? label : label}>
      <span>{button}</span>
    </Tooltip>
  );
};

/** Grupo de acciones por fila, alineadas y sin saltar de línea. */
export const RowActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>{children}</Box>
);

interface ConfirmOptions {
  title: React.ReactNode;
  message?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  variant?: ConfirmVariant;
}

/**
 * Confirmación con `ConfirmDialog` en lugar de `window.confirm`.
 * const [confirm, confirmDialog] = useConfirm(); if (!(await confirm({ title }))) return; … {confirmDialog}
 */
export const useConfirm = (): [(o: ConfirmOptions) => Promise<boolean>, React.ReactNode] => {
  const t = useT();
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);
  const close = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOpts(null);
  };
  const dialog = (
    <ConfirmDialog
      open={!!opts}
      title={opts?.title ?? ''}
      message={opts?.message}
      variant={opts?.variant ?? 'danger'}
      confirmLabel={opts?.confirmLabel ?? (opts?.variant && opts.variant !== 'danger' ? undefined : t('common.delete'))}
      cancelLabel={t('common.cancel')}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );
  return [confirm, dialog];
};

/** Mensaje de error del API o el texto por defecto. */
export const apiError = (error: unknown, fallback: string): string => {
  const access = accessErrorMessage(error);
  if (access) return access;
  const msg = (error as any)?.response?.data?.message;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
};

type Severity = 'success' | 'info' | 'warning' | 'error';

/**
 * Avisos con `useNotify()` de la librería, con la misma firma de `showAlert(message, severity)`
 * que usaban las pantallas (para no cambiar su lógica).
 */
export const useAlert = () => {
  const notify = useNotify();
  return useCallback(
    (message: string, severity: Severity = 'success') => {
      notify[severity](message);
    },
    [notify],
  );
};
